/**
 * Server MCP (ADR-0033 Phase E) — operator-configured Model Context Protocol providers
 * whose tools a custom agent can call. UNLIKE a sandbox tool (credential-free), an MCP
 * server reaches the NETWORK and may need credentials: the auth secret lives in a
 * reused `http_bearer`/`api_key`/`oauth2_client_credentials` credential (the vault), the
 * URL is SSRF-guarded, and the transport is HTTP (Streamable) or SSE (stdio deferred).
 * Includes a TEST-CONNECTION (connect + list the server's tools) usable before saving.
 * Attach to a custom agent on the Agents page. Under the `ai` macro-area.
 *
 * Native controls are intentional: this is the operator console.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Plug, Trash2 } from "lucide-react";

import { CredentialPicker } from "@/components/credentials/CredentialPicker";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { type CredentialType } from "@/lib/api-credentials";
import { usePermissions } from "@/lib/permissions";
import {
  type AiMcpOAuthStatus,
  type AiMcpServer,
  type AiMcpTestResult,
  mcpApi,
} from "@/lib/api-ai";

const inputCls = "rounded-md border border-border bg-background px-2 py-1.5 text-sm";
const btnCls =
  "inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-sm hover:bg-muted disabled:opacity-50";

const KEY = ["ai", "mcp-servers"] as const;

export function McpServersPage() {
  const { can } = usePermissions();
  const canWrite = can("mcp", "write");
  const canDelete = can("mcp", "delete");
  const qc = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: () => mcpApi.list() });

  const remove = useMutation({
    mutationFn: (id: string) => mcpApi.remove(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });

  const [editing, setEditing] = useState<AiMcpServer | null>(null);
  const [creating, setCreating] = useState(false);

  const servers = q.data?.servers ?? [];
  const transports = q.data?.transports ?? ["http"];
  // Backend-driven (the auth credential types whose secret becomes a header) — cast the
  // wire `string[]` to the FE literal union; the backend is the source of truth.
  const credentialTypes = (q.data?.credential_types ?? ["http_bearer", "api_key"]) as CredentialType[];

  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-lg font-semibold">
            <Plug className="h-4 w-4 text-muted-foreground" /> Server MCP
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Provider Model Context Protocol esterni i cui strumenti un agente
            personalizzato può usare. A differenza di uno strumento sandbox, un server
            MCP raggiunge la rete con le credenziali dell'operatore — ma non amplia mai
            l'accesso ai dati del ruolo. Collega un server a un agente dalla pagina Agenti.
          </p>
        </div>
        {canWrite && (
          <button type="button" className={btnCls} onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" /> Nuovo
          </button>
        )}
      </div>

      {q.isLoading && <p className="text-sm text-muted-foreground">Caricamento…</p>}
      {servers.length === 0 && !q.isLoading ? (
        <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          Nessun server MCP. Aggiungine uno per dare a un agente strumenti esterni
          (es. un server che cerca su un knowledge base o crea ticket).
        </p>
      ) : (
        servers.length > 0 && (
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2">Server</th>
                <th className="py-2">URL</th>
                <th className="py-2">Stato</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {servers.map((s) => (
                <tr key={s.id} className="border-b border-border/60 align-top">
                  <td className="py-2 font-medium">
                    {s.name}
                    <span className="ml-2 rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                      {s.key}
                    </span>
                    {s.description && (
                      <p className="mt-0.5 text-xs font-normal text-muted-foreground">
                        {s.description}
                      </p>
                    )}
                  </td>
                  <td className="py-2 font-mono text-xs text-muted-foreground">{s.url}</td>
                  <td className="py-2">
                    <span className={s.enabled ? "text-success text-xs" : "text-muted-foreground text-xs"}>
                      {s.enabled ? "attivo" : "disattivato"}
                    </span>
                  </td>
                  <td className="py-2">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        className="rounded p-1 hover:bg-muted"
                        title={canWrite ? "Modifica" : "Apri"}
                        onClick={() => setEditing(s)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      {canDelete && (
                        <button
                          type="button"
                          className="rounded p-1 text-destructive hover:bg-muted"
                          title="Elimina"
                          disabled={remove.isPending}
                          onClick={() => {
                            if (window.confirm(`Eliminare il server “${s.name}”?`)) remove.mutate(s.id);
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}

      {(creating || editing) && (
        <McpDialog
          server={editing}
          transports={transports}
          credentialTypes={credentialTypes}
          canWrite={canWrite}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => void qc.invalidateQueries({ queryKey: KEY })}
        />
      )}
    </div>
  );
}

function McpDialog({
  server,
  transports,
  credentialTypes,
  canWrite,
  onClose,
  onSaved,
}: {
  server: AiMcpServer | null;
  transports: string[];
  credentialTypes: CredentialType[];
  canWrite: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [savedId, setSavedId] = useState<string | null>(server?.id ?? null);
  const [key, setKey] = useState(server?.key ?? "");
  const [name, setName] = useState(server?.name ?? "");
  const [description, setDescription] = useState(server?.description ?? "");
  const [transport, setTransport] = useState(server?.transport ?? transports[0] ?? "http");
  const [url, setUrl] = useState(server?.url ?? "");
  const [credentialId, setCredentialId] = useState<string | null>(
    server?.headers_credential_id ?? null,
  );
  const [authMethod, setAuthMethod] = useState<string>(
    server?.auth_method ?? (server?.headers_credential_id ? "credential" : "none"),
  );
  const [oauthClientId, setOauthClientId] = useState(server?.oauth?.client_id ?? "");
  const [oauthClientSecret, setOauthClientSecret] = useState("");
  const [oauthScopes, setOauthScopes] = useState(server?.oauth?.scopes ?? "");
  const [toolPrefix, setToolPrefix] = useState(server?.tool_prefix ?? "");
  const [timeout, setTimeout] = useState(server?.timeout_seconds ?? 30);
  const [enabled, setEnabled] = useState(server?.enabled ?? true);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name,
        description: description || null,
        transport,
        url,
        // The credential only applies in `credential` mode — clear it otherwise so a
        // stale credential doesn't ride along after switching to OAuth / public.
        headers_credential_id: authMethod === "credential" ? credentialId : null,
        auth_method: authMethod,
        tool_prefix: toolPrefix || null,
        timeout_seconds: Number(timeout) || 30,
        enabled,
      };
      return savedId ? mcpApi.update(savedId, body) : mcpApi.create({ key, ...body });
    },
    onSuccess: (saved) => {
      setSavedId(saved.id);
      onSaved();
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "Errore"),
  });

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">
            {savedId ? "Modifica server MCP" : "Nuovo server MCP"}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-muted-foreground">Nome</span>
              <input className={`${inputCls} mt-1 w-full`} value={name}
                     onChange={(e) => setName(e.target.value)} placeholder="GitHub" />
            </label>
            <label className="block">
              <span className="text-xs text-muted-foreground">Chiave (prefisso strumenti, immutabile)</span>
              <input className={`${inputCls} mt-1 w-full font-mono`} value={key}
                     disabled={!!savedId}
                     onChange={(e) => setKey(e.target.value)} placeholder="github" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs text-muted-foreground">Descrizione</span>
            <input className={`${inputCls} mt-1 w-full`} value={description}
                   onChange={(e) => setDescription(e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs text-muted-foreground">URL (Streamable HTTP / SSE)</span>
            <input className={`${inputCls} mt-1 w-full font-mono`} value={url}
                   onChange={(e) => setUrl(e.target.value)} placeholder="https://mcp.example.com/mcp" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-muted-foreground">Trasporto</span>
              <select className={`${inputCls} mt-1 w-full`} value={transport}
                      onChange={(e) => setTransport(e.target.value)}>
                {transports.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-muted-foreground">Timeout (s)</span>
              <input type="number" className={`${inputCls} mt-1 w-full`} value={timeout}
                     onChange={(e) => setTimeout(Number(e.target.value))} />
            </label>
          </div>
          <div className="space-y-3 rounded-md border border-border p-3">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Autenticazione
            </div>
            <label className="block">
              <span className="text-xs text-muted-foreground">Metodo</span>
              <select className={`${inputCls} mt-1 w-full`} value={authMethod}
                      onChange={(e) => setAuthMethod(e.target.value)} disabled={!canWrite}>
                <option value="none">Nessuna (server pubblico)</option>
                <option value="credential">Credenziale (bearer / API key)</option>
                <option value="oauth">OAuth 2.1 (autorizzazione interattiva)</option>
              </select>
            </label>
            {authMethod === "credential" && (
              <label className="block">
                <span className="text-xs text-muted-foreground">Credenziale (auth header)</span>
                <div className="mt-1">
                  <CredentialPicker
                    value={credentialId}
                    onChange={setCredentialId}
                    type={credentialTypes}
                    placeholder="Seleziona una credenziale"
                  />
                </div>
              </label>
            )}
            {authMethod === "oauth" && (
              <OAuthPanel
                serverId={savedId}
                status={server?.oauth ?? null}
                clientId={oauthClientId}
                setClientId={setOauthClientId}
                clientSecret={oauthClientSecret}
                setClientSecret={setOauthClientSecret}
                scopes={oauthScopes}
                setScopes={setOauthScopes}
                canWrite={canWrite}
                onChanged={onSaved}
              />
            )}
          </div>
          <label className="block">
            <span className="text-xs text-muted-foreground">Prefisso strumenti (default: la chiave)</span>
            <input className={`${inputCls} mt-1 w-full font-mono`} value={toolPrefix}
                   onChange={(e) => setToolPrefix(e.target.value)} placeholder="(la chiave)" />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            Attivo
          </label>
          </div>

          {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

          {canWrite && (
            <TestConnectionPanel
              url={url}
              transport={transport}
              credentialId={credentialId}
              toolPrefix={toolPrefix}
              timeout={Number(timeout) || 30}
            />
          )}
        </DialogBody>

        <DialogFooter>
          <button type="button" className={btnCls} onClick={onClose}>Chiudi</button>
          {canWrite && (
            <button
              type="button"
              className={`${btnCls} bg-primary text-primary-foreground hover:bg-primary/90`}
              disabled={save.isPending || !name.trim() || (!savedId && !key.trim()) || !url.trim()}
              onClick={() => { setError(null); save.mutate(); }}
            >
              {savedId ? "Salva" : "Crea"}
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OAuthPanel({
  serverId,
  status,
  clientId,
  setClientId,
  clientSecret,
  setClientSecret,
  scopes,
  setScopes,
  canWrite,
  onChanged,
}: {
  serverId: string | null;
  status: AiMcpOAuthStatus | null;
  clientId: string;
  setClientId: (v: string) => void;
  clientSecret: string;
  setClientSecret: (v: string) => void;
  scopes: string;
  setScopes: (v: string) => void;
  canWrite: boolean;
  onChanged: () => void;
}) {
  const qc = useQueryClient();
  const [redirectUri, setRedirectUri] = useState<string | null>(status?.redirect_uri ?? null);
  const [err, setErr] = useState<string | null>(null);

  // Live status (after the popup closes / a disconnect); falls back to the row's state.
  const statusQ = useQuery({
    queryKey: ["ai", "mcp-oauth", serverId],
    queryFn: () => mcpApi.oauthStatus(serverId as string),
    enabled: !!serverId,
    initialData: status ?? undefined,
  });
  const live = statusQ.data ?? status;
  const connected = !!live?.connected;

  // The callback popup posts `{type:'mcp-oauth'}` when it lands — refresh the status.
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (e.data && (e.data as { type?: string }).type === "mcp-oauth") {
        void qc.invalidateQueries({ queryKey: ["ai", "mcp-oauth", serverId] });
        onChanged();
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [qc, serverId, onChanged]);

  const begin = useMutation({
    mutationFn: () =>
      mcpApi.oauthBegin(serverId as string, {
        client_id: clientId || null,
        client_secret: clientSecret || null,
        scopes: scopes || null,
      }),
    onSuccess: (r) => {
      setErr(null);
      setRedirectUri(r.redirect_uri);
      window.open(r.authorize_url, "_blank", "width=520,height=720");
    },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : "Errore"),
  });

  const disconnect = useMutation({
    mutationFn: () => mcpApi.oauthDisconnect(serverId as string),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ai", "mcp-oauth", serverId] });
      onChanged();
    },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : "Errore"),
  });

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs text-muted-foreground">Client ID (opzionale — DCR se vuoto)</span>
          <input className={`${inputCls} mt-1 w-full font-mono`} value={clientId}
                 onChange={(e) => setClientId(e.target.value)} placeholder="(registrazione dinamica)" />
        </label>
        <label className="block">
          <span className="text-xs text-muted-foreground">Client secret (lascia vuoto per non cambiarlo)</span>
          <input type="password" className={`${inputCls} mt-1 w-full font-mono`} value={clientSecret}
                 onChange={(e) => setClientSecret(e.target.value)} placeholder="••••" />
        </label>
      </div>
      <label className="block">
        <span className="text-xs text-muted-foreground">Scopes (separati da spazio, opzionale)</span>
        <input className={`${inputCls} mt-1 w-full font-mono`} value={scopes}
               onChange={(e) => setScopes(e.target.value)} placeholder="es. read:wiki write:docs" />
      </label>

      {!serverId ? (
        <p className="text-xs text-muted-foreground">Salva il server, poi torna qui per connetterti.</p>
      ) : (
        <div className="space-y-2 rounded-md bg-muted/40 p-2.5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs">
              Stato:{" "}
              <span className={connected ? "text-success" : "text-muted-foreground"}>
                {connected ? "connesso" : "non connesso"}
              </span>
              {connected && live?.expires_at && (
                <span className="text-muted-foreground"> · scade {new Date(live.expires_at).toLocaleString()}</span>
              )}
            </span>
            {canWrite && (
              <div className="flex gap-2">
                <button type="button" className={btnCls} disabled={begin.isPending}
                        onClick={() => { setErr(null); begin.mutate(); }}>
                  <Plug className="h-3.5 w-3.5" /> {connected ? "Riautorizza" : "Connetti"}
                </button>
                {connected && (
                  <button type="button" className={btnCls} disabled={disconnect.isPending}
                          onClick={() => { setErr(null); disconnect.mutate(); }}>
                    Disconnetti
                  </button>
                )}
              </div>
            )}
          </div>
          {redirectUri && (
            <p className="text-[11px] text-muted-foreground">
              Autorizza questo redirect URI nel provider:{" "}
              <code className="font-mono">{redirectUri}</code>
            </p>
          )}
        </div>
      )}
      {err && <p className="text-sm text-destructive">{err}</p>}
    </div>
  );
}

function TestConnectionPanel({
  url,
  transport,
  credentialId,
  toolPrefix,
  timeout,
}: {
  url: string;
  transport: string;
  credentialId: string | null;
  toolPrefix: string;
  timeout: number;
}) {
  const [result, setResult] = useState<AiMcpTestResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const test = useMutation({
    mutationFn: () =>
      mcpApi.test({
        url,
        transport,
        headers_credential_id: credentialId,
        tool_prefix: toolPrefix || null,
        timeout_seconds: timeout,
      }),
    onSuccess: (r) => { setResult(r); setErr(null); },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : "Errore"),
  });

  return (
    <div className="mt-5 border-t border-border pt-4">
      <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold">
        <Plug className="h-3.5 w-3.5" /> Prova connessione
      </h4>
      <button
        type="button"
        className={btnCls}
        disabled={test.isPending || !url.trim()}
        onClick={() => { setResult(null); test.mutate(); }}
      >
        <Plug className="h-3.5 w-3.5" /> {test.isPending ? "Connessione…" : "Connetti e lista strumenti"}
      </button>

      {err && <p className="mt-2 text-sm text-destructive">{err}</p>}
      {result && (
        <div className="mt-3 text-xs">
          {result.ok ? (
            <div className="rounded-md border border-success/40 bg-success/5 p-2">
              <p className="font-medium text-success">
                Connesso — {result.tools_count} strumenti
              </p>
              {result.tool_names.length > 0 && (
                <p className="mt-1 font-mono text-muted-foreground">
                  {result.tool_names.join(", ")}
                </p>
              )}
            </div>
          ) : (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-destructive">
              <p className="font-medium">Connessione fallita</p>
              {result.error && <p className="mt-1 break-all">{result.error}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
